import { VuMediChatbotInline, _SUMMARIZE_VIDEO_QUESTION } from 'Chatbot';
import { getInterstitialStates } from 'Utils/dynamic-state';
import { VaiaProductTour } from 'Utils/product-tours';
import { isMobile, isMobileLandscape } from 'Utils/viewport';
import { UserStore } from 'Main/user-store';
import { AIChatbotEvents, trackAIChatbotEvent } from 'Analytics/ai-chatbot-events';

const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;

const _IN_VIDEO_PROMPT_DELAY = 3000;
const _IN_VIDEO_PROMPT_INITIAL = 'Summarize this video with Vumedi AI';

const InVideoPromptBlocker = {
    MOBILE: 'mobile',
    ALREADY_SEEN: 'already_seen',
    INTERSTITIAL: 'interstitial',
    PRODUCT_TOUR: 'product_tour',
};

/**
 * Show AI assistant tooltip, product tour.
 *
 * If forced = true, a smaller version of the tooltip is displayed. This smaller version is
 * used to display AI features to users that have already seen the whole product tour.
 */

const showProductTour = (forced = false) => {
    // Do not show the tour if in mobile landscape mode
    if (isMobileLandscape()) {
        return;
    }

    const tour = VaiaProductTour(forced);

    window.setTimeout(() => {
        const handleTourDone = () => {
            // If the tour was canceled due to landscape mode, do not mark as seen
            if (tour.landscapeCancelled) {
                return;
            }

            trackAIChatbotEvent(AIChatbotEvents.TOOLTIP_CLICKED);
            UserStore.saveVAIATooltipSeen(true);
        };

        tour.on('cancel', handleTourDone);
        tour.on('complete', handleTourDone);

        tour.start();
    }, 500);
};

export function setupChatbot(brightcovePlayer) {
    return new Promise((resolve) => {
        const container = document.querySelector('.js_vmChatbotInline');
        const mobileTriggerContainer = document.querySelector('.js_vmChatbotInlineMobileTrigger');

        if (!container || !mobileTriggerContainer) {
            resolve(false);
            return;
        }

        // `null` if decision is not resolved, true|false otherwise
        const shouldBlockExtraUI = () => {
            const states = getInterstitialStates();

            if (states.findIndex((state) => typeof state.data === 'undefined') > -1) {
                return null;
            }

            // If at least one `state.data` is `true`, tooltip should be blocked because
            // interstitial is displayed
            return states.reduce((acc, state) => acc || state.data, false);
        };

        const setInVideoPrompts = (chatbot) => {
            if (isMobile()) {
                trackAIChatbotEvent(AIChatbotEvents.IN_VIDEO_PROMPT_BLOCKED, {
                    assistant_type: chatbot.eventAssistantType,
                    video_id: context?.videoId || null,
                    video_slug: context?.videoSlug || null,
                    blocker: InVideoPromptBlocker.MOBILE,
                });
                return;
            }

            const inVideoPromptSeen = UserStore.loadInVideoPromptSeen(context.videoId);
            const currentTime = brightcovePlayer._player.currentTime() * 1000;

            if (inVideoPromptSeen) {
                trackAIChatbotEvent(AIChatbotEvents.IN_VIDEO_PROMPT_BLOCKED, {
                    assistant_type: chatbot.eventAssistantType,
                    video_id: context?.videoId || null,
                    video_slug: context?.videoSlug || null,
                    blocker: InVideoPromptBlocker.ALREADY_SEEN,
                });
                return;
            }

            if (currentTime >= _IN_VIDEO_PROMPT_DELAY) {
                brightcovePlayer.showInVideoPrompt(_IN_VIDEO_PROMPT_INITIAL);
            } else {
                window.setTimeout(() => {
                    brightcovePlayer.showInVideoPrompt(_IN_VIDEO_PROMPT_INITIAL);
                }, _IN_VIDEO_PROMPT_DELAY - currentTime);
            }

            brightcovePlayer.onInVideoPrompt = (prompt) => {
                if (prompt === _IN_VIDEO_PROMPT_INITIAL) {
                    chatbot._askQuestion(_SUMMARIZE_VIDEO_QUESTION, 'predefined');
                }
            };

            UserStore.saveInVideoPromptSeen(context.videoId);
        };

        const finalize = (chatbot, initialized, shouldBlockExtraUI) => {
            if (shouldBlockExtraUI) {
                if (initialized) {
                    trackAIChatbotEvent(AIChatbotEvents.IN_VIDEO_PROMPT_BLOCKED, {
                        assistant_type: chatbot.eventAssistantType,
                        video_id: context?.videoId || null,
                        video_slug: context?.videoSlug || null,
                        blocker: InVideoPromptBlocker.INTERSTITIAL,
                    });
                }
                resolve(false);
                return;
            }

            if (initialized) {
                // The final check before showing the tooltip is done by checking
                // if the server also said to display the product tour (tooltip)
                if (chatbot.showProductTour) {
                    trackAIChatbotEvent(AIChatbotEvents.IN_VIDEO_PROMPT_BLOCKED, {
                        assistant_type: chatbot.eventAssistantType,
                        video_id: context?.videoId || null,
                        video_slug: context?.videoSlug || null,
                        blocker: InVideoPromptBlocker.PRODUCT_TOUR,
                    });
                    // If a tooltip has been seen by the user, force the minimal version
                    showProductTour(tooltipSeen);
                    resolve(true);
                    return;
                }

                setInVideoPrompts(chatbot);
            }

            resolve(false);
        };

        // Determine whether the client app wants to show the tooltip
        const tooltipSeen = UserStore.loadVAIATooltipSeen();
        const tooltipForced = new URL(window.location.href).searchParams.get('ai-summary') !== null;
        const showTooltip = !tooltipSeen || tooltipForced;

        const chatbot = new VuMediChatbotInline(
            parseInt(context.videoId, 10),
            context.viewedUUID,
            brightcovePlayer,
            context.videoSlug,
            showTooltip,
        );

        chatbot
            .initialize(container, mobileTriggerContainer)
            .then((initialized) => {
                const handleDynamicStateChange = () => {
                    const shouldBlock = shouldBlockExtraUI();

                    if (shouldBlock !== null) {
                        finalize(chatbot, initialized, shouldBlock);
                        window.removeEventListener('dynamicstatechange', handleDynamicStateChange);
                    }
                };

                const shouldBlock = shouldBlockExtraUI();

                if (shouldBlock === null) {
                    window.addEventListener('dynamicstatechange', handleDynamicStateChange);
                } else {
                    finalize(chatbot, initialized, shouldBlock);
                }
            })
            .catch(() => resolve(false));
    });
}
